import { customElement, html, css, cssManager, property, state, DeesElement } from '@design.estate/dees-element';
import '../../00group-button/dees-button/dees-button.js';
import { themeDefaultStyles } from '../../00theme.js';
import type { IStorageDataProvider } from './interfaces.js';
import { formatSize, getFileName } from './utilities.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-storage-preview': DeesStoragePreview;
  }
}

@customElement('dees-storage-preview')
export class DeesStoragePreview extends DeesElement {
  @property({ type: Object })
  public accessor dataProvider: IStorageDataProvider | null = null;

  @property({ type: String })
  public accessor bucketName: string = '';

  @property({ type: String })
  public accessor objectKey: string = '';

  @state()
  private accessor loading: boolean = false;

  @state()
  private accessor saving: boolean = false;

  @state()
  private accessor content: string = '';

  @state()
  private accessor originalTextContent: string = '';

  @state()
  private accessor hasChanges: boolean = false;

  @state()
  private accessor editing: boolean = false;

  @state()
  private accessor contentType: string = '';

  @state()
  private accessor fileSize: number = 0;

  @state()
  private accessor lastModified: string = '';

  @state()
  private accessor error: string = '';

  @state() private accessor saveError = '';
  private loadGeneration = 0;
  private reloadOnReconnect = false;

  public static styles = [
    cssManager.defaultStyles,
    themeDefaultStyles,
    css`
      :host {
        display: block;
        height: 100%;
        min-height: 0;
        min-width: 0;
        font: 13px/1.5 var(--dees-font-family);
      }

      .preview-container {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .preview-header {
        padding: 10px 16px;
        border-bottom: 1px solid var(--dees-color-border-subtle);
      }

      .preview-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 28px; }
      .preview-heading slot { flex-shrink: 0; }
      .preview-title {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .preview-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 12px;
        font-size: 11px;
        color: var(--dees-color-text-muted);
      }

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .preview-content {
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      .preview-content dees-preview {
        width: 100%;
        height: 100%;
      }

      .preview-content.code-editor {
        padding: 0;
        overflow: hidden;
      }

      .preview-content.code-editor dees-input-code {
        height: 100%;
      }

      .preview-actions {
        padding: 12px 16px;
        border-top: 1px solid var(--dees-color-border-default);
        display: flex;
        gap: 8px;
      }

      button { font-family: inherit; }
      button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }
      @media (pointer: coarse) { button { min-height: 44px; } .preview-actions { flex-wrap: wrap; } }
      .preview-actions { justify-content: flex-end; flex-wrap: wrap; }
      dees-button.danger::part(button) { color: var(--dees-color-text-error); }
      .unsaved-indicator {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: var(--dees-color-badge-warning-bg);
        border: 0;
        border-radius: 4px;
        font-size: 12px;
        color: var(--dees-color-text-warning);
      }

      .unsaved-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--dees-color-text-muted);
        text-align: center;
        padding: 24px;
      }

      .empty-state svg {
        width: 48px;
        height: 48px;
        margin-bottom: 12px;
        opacity: 0.5;
      }

      .loading-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--dees-color-text-muted);
      }

      .error-state {
        padding: 16px;
        color: var(--dees-color-text-error);
        text-align: center;
      }
    `,
  ];

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('objectKey') || changedProperties.has('bucketName') || changedProperties.has('dataProvider')) {
      this.loadGeneration++;
      if (this.objectKey) {
        this.loadObject();
      } else {
        this.content = '';
        this.contentType = '';
        this.error = '';
        this.originalTextContent = '';
        this.hasChanges = false;
        this.editing = false;
      }
    }
  }

  private async loadObject() {
    if (!this.objectKey || !this.bucketName || !this.dataProvider) return;

    const generation = ++this.loadGeneration;
    this.loading = true;
    this.error = '';
    this.hasChanges = false;
    this.saveError = '';
    this.editing = false;

    try {
      const result = await this.dataProvider.getObject(this.bucketName, this.objectKey);
      if (generation !== this.loadGeneration || !this.isConnected) return;
      if (!result) {
        this.error = 'Object not found';
        this.loading = false;
        return;
      }
      this.content = result.content || '';
      this.contentType = result.contentType || '';
      this.fileSize = result.size || 0;
      this.lastModified = result.lastModified || '';

      if (this.isText()) {
        this.originalTextContent = this.getTextContent();
      }
    } catch (err) {
      if (generation !== this.loadGeneration || !this.isConnected) return;
      console.error('Error loading object:', err);
      this.error = 'Failed to load object';
    }

    this.loading = false;
  }

  /** Guard navigation while an edit or write is in progress. */
  public canLeave(): boolean {
    return !this.saving && (!this.hasChanges || confirm('Discard unsaved changes?'));
  }

  async connectedCallback() {
    await super.connectedCallback();
    if (this.reloadOnReconnect && this.isConnected) {
      this.reloadOnReconnect = false;
      await this.loadObject();
    }
  }

  async disconnectedCallback() {
    this.reloadOnReconnect = this.loading;
    this.loadGeneration++;
    await super.disconnectedCallback();
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  }

  private isImage(): boolean {
    return this.contentType.startsWith('image/');
  }

  private isText(): boolean {
    return (
      this.contentType.startsWith('text/') ||
      this.contentType === 'application/json' ||
      this.contentType === 'application/xml' ||
      this.contentType === 'application/javascript'
    );
  }

  private getTextContent(): string {
    try {
      const binaryString = atob(this.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch {
      return 'Unable to decode content';
    }
  }

  private async handleDownload() {
    try {
      const blob = new Blob([Uint8Array.from(atob(this.content), (c) => c.charCodeAt(0))], {
        type: this.contentType,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileName(this.objectKey);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading:', err);
    }
  }

  private async handleDelete() {
    if (!this.dataProvider || this.saving) return;
    if (!confirm(`Delete "${getFileName(this.objectKey)}"?`)) return;

    const key = this.objectKey;
    this.saving = true;
    try {
      const deleted = await this.dataProvider.deleteObject(this.bucketName, key);
      if (!deleted) throw new Error('Delete was not accepted');
      this.dispatchEvent(
        new CustomEvent('object-deleted', {
          detail: { key },
          bubbles: true,
          composed: true,
        })
      );
    } catch (err) {
      console.error('Error deleting object:', err);
      this.error = 'Unable to delete this file. Try again.';
    } finally { this.saving = false; }
  }

  private getLanguage(): string {
    const ext = this.objectKey.split('.').pop()?.toLowerCase() || '';
    const languageMap: Record<string, string> = {
      ts: 'typescript',
      tsx: 'typescript',
      js: 'javascript',
      jsx: 'javascript',
      mjs: 'javascript',
      cjs: 'javascript',
      json: 'json',
      html: 'html',
      htm: 'html',
      css: 'css',
      scss: 'scss',
      sass: 'scss',
      less: 'less',
      md: 'markdown',
      markdown: 'markdown',
      xml: 'xml',
      yaml: 'yaml',
      yml: 'yaml',
      py: 'python',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
      java: 'java',
      c: 'c',
      cpp: 'cpp',
      h: 'c',
      hpp: 'cpp',
      cs: 'csharp',
      php: 'php',
      sh: 'shell',
      bash: 'shell',
      zsh: 'shell',
      sql: 'sql',
      graphql: 'graphql',
      gql: 'graphql',
      dockerfile: 'dockerfile',
      txt: 'plaintext',
    };
    return languageMap[ext] || 'plaintext';
  }

  private handleContentChange(event: CustomEvent) {
    const newValue = event.detail as string;
    this.hasChanges = newValue !== this.originalTextContent;
  }

  private handleEdit() {
    this.editing = true;
  }

  private handleDiscard() {
    const codeEditor = this.shadowRoot?.querySelector('dees-input-code') as any;
    if (codeEditor) {
      codeEditor.value = this.originalTextContent;
    }
    this.hasChanges = false;
    this.saveError = '';
    this.editing = false;
  }

  private async handleSave() {
    if (!this.hasChanges || this.saving || !this.dataProvider) return;

    this.saving = true;
    this.saveError = '';
    const generation = this.loadGeneration;

    try {
      const codeEditor = this.shadowRoot?.querySelector('dees-input-code') as any;
      const currentContent = codeEditor?.value ?? '';

      const encoder = new TextEncoder();
      const bytes = encoder.encode(currentContent);
      const base64Content = btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));

      const success = await this.dataProvider.putObject(
        this.bucketName,
        this.objectKey,
        base64Content,
        this.contentType
      );

      if (generation !== this.loadGeneration) return;
      if (!success) throw new Error('Save was not accepted');
      if (success) {
        this.originalTextContent = currentContent;
        this.hasChanges = false;
        this.editing = false;
        this.content = base64Content;
      }
    } catch (err) {
      if (generation === this.loadGeneration) {
        console.error('Error saving object:', err);
        this.saveError = 'Unable to save. Your edits are still available. Try again.';
      }
    } finally { this.saving = false; }
  }

  private renderHeader() {
    return html`
        <div class="preview-header">
          <div class="preview-heading"><div class="preview-title" title=${this.objectKey}>${getFileName(this.objectKey)}</div><slot name="header-actions"></slot></div>
          ${!this.loading && !this.error ? html`<div class="preview-meta" title=${this.contentType}>

            <span class="meta-item">${formatSize(this.fileSize)}</span>
            <span class="meta-item">${this.formatDate(this.lastModified)}</span>
            ${this.hasChanges ? html`
              <span class="unsaved-indicator">
                <span class="unsaved-dot"></span>
                Unsaved changes
              </span>
            ` : ''}
          </div>` : ''}
        </div>`;
  }

  render() {
    if (!this.objectKey) {
      return html`
        <div class="preview-container">
          <div class="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <p>Select a file to preview</p>
          </div>
        </div>
      `;
    }

    if (this.loading) {
      return html`
        <div class="preview-container">
          ${this.renderHeader()}<div class="loading-state" role="status">Loading…</div>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="preview-container">
          ${this.renderHeader()}<div class="error-state" role="alert">${this.error}<dees-button text="Retry" @clicked=${this.loadObject}></dees-button></div>
        </div>
      `;
    }

    return html`
      <div class="preview-container">
        ${this.renderHeader()}

        <div class="preview-content ${this.editing ? 'code-editor' : ''}">
          ${this.editing
            ? html`
                <dees-input-code
                  .value=${this.originalTextContent}
                  .language=${this.getLanguage()}
                  height="100%"
                  @content-change=${(e: CustomEvent) => this.handleContentChange(e)}
                ></dees-input-code>
              `
            : this.isText()
              ? html`
                  <dees-preview .frameless=${true}
                    .textContent=${this.originalTextContent}
                    .filename=${getFileName(this.objectKey)}
                    .language=${this.getLanguage()}
                    .showToolbar=${true}
                    .showFilename=${false}
                  ></dees-preview>
                `
              : html`
                  <dees-preview .frameless=${true}
                    .base64=${this.content}
                    .mimeType=${this.contentType}
                    .filename=${getFileName(this.objectKey)}
                    .showToolbar=${true}
                    .showFilename=${false}
                  ></dees-preview>
                `
          }
        </div>

        ${this.saveError ? html`<div class="error-state" role="alert">${this.saveError}</div>` : ''}
        <div class="preview-actions">
          ${this.editing ? html`
            <dees-button size="sm" type="ghost" .text=${this.hasChanges ? 'Discard' : 'Cancel'} .disabled=${this.saving} @clicked=${this.handleDiscard}></dees-button>
            <dees-button size="sm" .text=${this.saving ? 'Saving…' : 'Save'} .disabled=${this.saving || !this.hasChanges} @clicked=${this.handleSave}></dees-button>
          ` : html`
            <dees-button size="sm" type="ghost" class="danger" text="Delete" .disabled=${this.saving} @clicked=${this.handleDelete}></dees-button>
            <dees-button size="sm" type="outline" text="Download" .disabled=${this.saving} @clicked=${this.handleDownload}></dees-button>
            ${this.isText() ? html`<dees-button size="sm" type="outline" text="Edit" .disabled=${this.saving} @clicked=${this.handleEdit}></dees-button>` : ''}
          `}
        </div>
      </div>
    `;
  }
}
